import { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import { LuBold, LuItalic, LuList, LuListOrdered, LuListChecks, LuImage } from 'react-icons/lu';
import './ObservationEditor.css';

interface Props {
    /** HTML inicial. Muda só quando outra anotação entra em edição (use `key`). */
    valor: string;
    onChange: (html: string) => void;
    /** Arquivos que NÃO são imagem, colados ou arrastados no texto, viram anexos da anotação. */
    onFiles?: (arquivos: File[]) => void;
    /**
     * Imagem colada, arrastada ou escolhida pelo botão: sobe e devolve a URL
     * para o `src`. Sem esta prop, imagem cai em `onFiles` como qualquer arquivo.
     */
    onImage?: (arquivo: File) => Promise<string | null>;
    placeholder?: string;
    disabled?: boolean;
    autoFocus?: boolean;
}

const ehImagem = (f: File) => f.type.startsWith('image/');

/**
 * Editor de texto da anotação da oportunidade (2026-09-13).
 *
 * É o mesmo TipTap do módulo Anotações (`NoteEditor`), com a barra reduzida
 * ao que uma nota de negociação usa — negrito, itálico, listas, tarefas e
 * imagem. A imagem colada no corpo sobe pelo `onImage` (na oportunidade ela
 * é um anexo da anotação servido pela API) e entra inline; outro arquivo
 * colado ou arrastado dispara `onFiles` e vira anexo fora do texto. Salva
 * HTML, como as Anotações; anotações antigas em texto puro continuam texto.
 * Prefixo `oe-`.
 */
const ObservationEditor = ({ valor, onChange, onFiles, onImage, placeholder, disabled = false, autoFocus = false }: Props) => {
    const inputImagemRef = useRef<HTMLInputElement | null>(null);

    const editor = useEditor({
        extensions: [
            StarterKit,
            Placeholder.configure({ placeholder: placeholder ?? 'O que aconteceu nesta conversa, ligação ou reunião…' }),
            Image.configure({ inline: false, allowBase64: false }),
            TaskList,
            TaskItem.configure({ nested: true }),
        ],
        content: valor || '',
        editable: !disabled,
        autofocus: autoFocus ? 'end' : false,
        onUpdate: ({ editor: e }) => onChange(e.isEmpty ? '' : e.getHTML()),
        editorProps: {
            attributes: { class: 'oe-doc' },
            handlePaste: (_view, event) => receberArquivos(Array.from(event.clipboardData?.files ?? []), event),
            handleDrop: (_view, event) => receberArquivos(Array.from(event.dataTransfer?.files ?? []), event),
        },
    });

    /* Imagem vai para o texto; o resto vira anexo. Devolve true quando tratou
       o evento, para o ProseMirror não colar o arquivo como texto. */
    function receberArquivos(arquivos: File[], event: Event): boolean {
        if (arquivos.length === 0) return false;
        event.preventDefault();
        const imagens = onImage ? arquivos.filter(ehImagem) : [];
        const outros = arquivos.filter(a => !imagens.includes(a));
        imagens.forEach(inserirImagem);
        if (outros.length > 0 && onFiles) onFiles(outros);
        return true;
    }

    async function inserirImagem(arquivo: File) {
        if (!onImage || !editor) return;
        const url = await onImage(arquivo);
        if (url) editor.chain().focus().setImage({ src: url, alt: arquivo.name }).run();
    }

    useEffect(() => { editor?.setEditable(!disabled); }, [editor, disabled]);

    if (!editor) return null;

    const botao = (ativo: boolean, rotulo: string, icone: React.ReactNode, acao: () => void) => (
        <button type="button" className={`oe-bt ${ativo ? 'is-on' : ''}`} aria-label={rotulo} title={rotulo} aria-pressed={ativo}
            disabled={disabled} onMouseDown={e => e.preventDefault()} onClick={acao}>
            {icone}
        </button>
    );

    return (
        <div className={`oe ${disabled ? 'is-disabled' : ''}`}>
            <div className="oe-bar" role="toolbar" aria-label="Formatação">
                {botao(editor.isActive('bold'), 'Negrito', <LuBold size={14} />, () => editor.chain().focus().toggleBold().run())}
                {botao(editor.isActive('italic'), 'Itálico', <LuItalic size={14} />, () => editor.chain().focus().toggleItalic().run())}
                <span className="oe-sep" aria-hidden="true" />
                {botao(editor.isActive('bulletList'), 'Lista', <LuList size={14} />, () => editor.chain().focus().toggleBulletList().run())}
                {botao(editor.isActive('orderedList'), 'Lista numerada', <LuListOrdered size={14} />, () => editor.chain().focus().toggleOrderedList().run())}
                {botao(editor.isActive('taskList'), 'Tarefas', <LuListChecks size={14} />, () => editor.chain().focus().toggleTaskList().run())}
                {onImage && (
                    <>
                        <span className="oe-sep" aria-hidden="true" />
                        {botao(false, 'Imagem no texto', <LuImage size={14} />, () => inputImagemRef.current?.click())}
                        <input ref={inputImagemRef} type="file" accept="image/*" hidden
                            onChange={e => { Array.from(e.target.files ?? []).forEach(inserirImagem); e.target.value = ''; }} />
                    </>
                )}
            </div>
            <EditorContent editor={editor} />
        </div>
    );
};

export default ObservationEditor;
